import { makeObservable, action } from 'mobx';
import { Tool, type ToolContext } from './base/Tool';
import type { SvgElement, Element as CanvasElement } from '@/types';

export class SvgTool extends Tool {
  constructor() {
    super('svg', 'SVG');
    makeObservable(this, {
      onStart: action,
      onMove: action,
      onEnd: action,
    });
  }

  onStart(_context: ToolContext): CanvasElement | null {
    // SVG tool doesn't create elements on start - handled by clipboard paste
    return null;
  }

  onMove(_context: ToolContext): CanvasElement | undefined {
    // No preview for SVG tool
    return undefined;
  }

  onEnd(_context: ToolContext): CanvasElement | void {
    // No direct element creation, handled by clipboard paste
    return;
  }

  getCursor(): string {
    return 'default';
  }

  /**
   * Create an SVG element from clipboard content
   */
  createSvgElement(
    svgContent: string,
    x: number,
    y: number,
    width: number = 100,
    height: number = 100,
    style: any
  ): SvgElement {
    return {
      id: this.generatePreviewId(),
      type: 'svg',
      x,
      y,
      width,
      height,
      svgContent,
      style: {
        ...style,
        fillColor: 'transparent', // SVG elements never have background colors
        fillPattern: 'solid' as const,
        strokeWidth: 1, // Default stroke but won't be applied to SVG content
        strokeStyle: 'solid' as const,
      },
      isSelected: false,
      zIndex: 0,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  }

  /**
   * Check if clipboard contains SVG content
   */
  static async isSvgInClipboard(): Promise<boolean> {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (item.types.includes('text/plain')) {
          const text = await (await item.getType('text/plain')).text();
          return text.trim().startsWith('<svg') || text.includes('</svg>');
        }
      }
      return false;
    } catch (error) {
      // Fallback: try reading text directly
      try {
        const text = await navigator.clipboard.readText();
        return text.trim().startsWith('<svg') || text.includes('</svg>');
      } catch {
        return false;
      }
    }
  }

  /**
   * Get SVG content from clipboard
   */
  static async getSvgFromClipboard(): Promise<string | null> {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim().startsWith('<svg') || text.includes('</svg>')) {
        return text;
      }
      return null;
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      return null;
    }
  }
}
